import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Single localStorage JSON per (entityType, entityId-or-'new') — the source of
 * truth for unsaved changes AND the payload the AI assistant reads as its
 * context for the current form state. Matches the WorkflowAssistant pattern
 * (useWorkflowForm + buildWorkflowAssistantPrompt), see ENTITY_CREATION_WIZARD_SPEC.md §13.1.
 *
 * Write-through cycle:
 *   form field change → RHF watch() → setDraft(next) → localStorage write
 *                                                   ↓
 *                                         AssistantAssistant reads this key
 *                                         on every turn to build context.
 *
 * Clear on successful server save OR user-initiated Discard. Persist across
 * navigation so accidental leaves don't cost work.
 */
export function useEntityDraft<T extends Record<string, unknown>>(
  entityType: string,
  entityId: string | number | 'new',
  initial: T,
) {
  const key = `entity-draft:${entityType}:${entityId}`;

  const [draft, setDraftState] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return initial;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return { ...initial, ...parsed } as T;
      return initial;
    } catch {
      return initial;
    }
  });

  // Skip the first effect cycle so mount doesn't immediately rewrite identical content.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    try {
      window.localStorage.setItem(key, JSON.stringify(draft));
    } catch {
      /* quota exceeded — ignore */
    }
  }, [key, draft]);

  const setDraft = useCallback((next: T) => {
    setDraftState(next);
  }, []);

  const patchDraft = useCallback((patch: Partial<T>) => {
    setDraftState((prev) => ({ ...prev, ...patch }) as T);
  }, []);

  const clearDraft = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    setDraftState(initial);
  }, [key, initial]);

  return { draft, setDraft, patchDraft, clearDraft, storageKey: key };
}
